import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  CssBaseline,
  Box,
  Button,
  Typography,
  GlobalStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

import AddMaintenance from "./components/AddMaintenance";
import MaintenanceList from "./components/MaintenanceList";
import MaintenanceRecommendations from "./components/MaintenanceRecommendations";
import AutoLoggAssistant from "./components/AutoLoggAssistant";
import RemindersSection from "./components/RemindersSection";
import Login from "./components/Login";
import Sidebar from "./components/Sidebar";
import VehicleHeroCard from "./components/VehicleHeroCard";
import InsightsDashboard from "./components/InsightsDashboard";
import ConfirmDialog from "./components/ConfirmDialog";
import { auth } from "./firebase";
import { ThemeToggleContext } from "./ThemeContext";
import { useSnackbar } from "./SnackbarContext";
import API_BASE from "./config";
import { SERVICE_TYPE_OPTIONS } from "./constants/serviceTypes";

function App() {
  const [user, setUser] = useState(null);
  const [records, setRecords] = useState([]);
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [issueLogs, setIssueLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("maintenance");
  const [carsLoading, setCarsLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const { mode, setMode } = useContext(ThemeToggleContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { showSuccess, showError } = useSnackbar();

  const serviceTypeOptions = SERVICE_TYPE_OPTIONS;

  const askConfirm = ({ title, message, confirmLabel, destructive, onConfirm }) => {
    setConfirmState({ title, message, confirmLabel, destructive, onConfirm });
  };

  const handleConfirmAccept = () => {
    const action = confirmState?.onConfirm;
    setConfirmState(null);
    if (action) action();
  };

  const handleAddRecord = (savedRecord) => {
    setRecords((prev) => [savedRecord, ...prev]);
    showSuccess("Maintenance record added.");
  };

  const handleDeleteRecord = (id) => {
    askConfirm({
      title: "Delete maintenance record?",
      message: "This will permanently delete this maintenance record. This action cannot be undone.",
      confirmLabel: "Delete Record",
      destructive: true,
      onConfirm: () => performDeleteRecord(id),
    });
  };

  const performDeleteRecord = async (id) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/records/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to delete record");

      setRecords((prev) => prev.filter((record) => record.id !== id));
      showSuccess("Maintenance record deleted.");
    } catch (error) {
      console.error("Delete error:", error);
      showError("Couldn't delete that record. Please try again.");
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord({
      ...record,
      service_type: record.service_type || record.serviceType || "",
      mileage: record.mileage,
      date: record.date,
      cost: record.cost ?? record.price ?? record.amount ?? "",
    });
  };

  const handleSaveEditedRecord = async (updatedRecord) => {
    try {
      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE}/records/${updatedRecord.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          serviceType: updatedRecord.service_type,
          mileage: updatedRecord.mileage,
          date: updatedRecord.date,
          cost: updatedRecord.cost === "" ? null : updatedRecord.cost,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update record");
      }

      const savedRecord = await response.json();

      setRecords((prev) =>
        prev.map((record) =>
          record.id === savedRecord.id ? savedRecord : record
        )
      );

      setEditingRecord(null);
      showSuccess("Maintenance record updated.");
    } catch (error) {
      console.error("Edit error:", error);
      showError("Couldn't save your changes. Please try again.");
    }
  };

  const fetchCars = async (user) => {
    setCarsLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/cars`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Fetch cars failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      setCars(data);

      if (data.length > 0) setSelectedCar(data[0]);
    } catch (error) {
      console.error("Error fetching cars:", error);
      showError("Couldn't load your cars. Check your connection and try again.");
    } finally {
      setCarsLoading(false);
    }
  };

  const handleDeleteCar = async (carId) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/cars/${carId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
      }

      setCars((prev) => prev.filter((c) => c.id !== carId));

      setSelectedCar((prevSelected) => {
        if (!prevSelected || prevSelected.id !== carId) return prevSelected;

        const remaining = cars.filter((c) => c.id !== carId);
        return remaining.length ? remaining[0] : null;
      });

      setRecords([]);
      showSuccess("Car deleted.");
    } catch (e) {
      console.error("Delete car error:", e);
      showError("Couldn't delete that car. Please try again.");
    }
  };

  const handleDeleteCarRequest = () => {
    if (!selectedCar) return;
    const label = selectedCar.nickname || `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}`;
    askConfirm({
      title: "Delete this car?",
      message: `This will permanently delete ${label} and all of its maintenance records. This action cannot be undone.`,
      confirmLabel: "Delete Car",
      destructive: true,
      onConfirm: () => handleDeleteCar(selectedCar.id),
    });
  };

  const handleCreateReminder = async (reminder) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/reminders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: reminder.title,
          dueDate: reminder.dueDate || null,
          dueMileage: reminder.dueMileage || null,
          note: reminder.note || null,
          vehicleId: reminder.vehicleId || selectedCar?.id || null,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to save reminder: ${text}`);
      }

      const savedReminder = await response.json();
      setReminders((prev) => [savedReminder, ...prev]);
      showSuccess("Reminder saved.");
      return savedReminder;
    } catch (error) {
      console.error("Reminder save error:", error);
      showError("Couldn't save that reminder. Please try again.");
      throw error;
    }
  };

  const handleUpdateReminder = async (reminder) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/reminders/${reminder.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reminder),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to update reminder: ${text}`);
      }

      const updatedReminder = await response.json();
      setReminders((prev) =>
        prev.map((item) => (item.id === updatedReminder.id ? updatedReminder : item))
      );
      showSuccess("Reminder updated.");
      return updatedReminder;
    } catch (error) {
      console.error("Reminder update error:", error);
      showError("Couldn't update that reminder. Please try again.");
      throw error;
    }
  };

  const handleDeleteReminder = (reminderId) => {
    askConfirm({
      title: "Delete this reminder?",
      message: "This action cannot be undone.",
      confirmLabel: "Delete Reminder",
      destructive: true,
      onConfirm: () => performDeleteReminder(reminderId),
    });
  };

  const performDeleteReminder = async (reminderId) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/reminders/${reminderId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to delete reminder: ${text}`);
      }

      setReminders((prev) => prev.filter((item) => item.id !== reminderId));
      showSuccess("Reminder deleted.");
    } catch (error) {
      console.error("Reminder delete error:", error);
      showError("Couldn't delete that reminder. Please try again.");
    }
  };

  const handleCreateIssue = (issue) => {
    const newIssue = {
      id: Date.now(),
      date: new Date().toISOString().split("T")[0],
      status: "open",
      possibleCauses: issue.possibleCauses || [],
      ...issue,
    };
    setIssueLogs((prev) => [newIssue, ...prev]);
  };

  const currentMileage = useMemo(
    () => Math.max(0, ...records.map((record) => Number(record.mileage) || 0)),
    [records]
  );

  const fetchRecords = async (user, vehicleId) => {
    setRecordsLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/records?vehicleId=${vehicleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch records");

      const data = await response.json();
      setRecords(data);
    } catch (error) {
      console.error("Error fetching records:", error);
      showError("Couldn't load maintenance records.");
    } finally {
      setRecordsLoading(false);
    }
  };

  const fetchReminders = async (user, vehicleId) => {
    try {
      const token = await user.getIdToken();
      const url = vehicleId
        ? `${API_BASE}/reminders?vehicleId=${vehicleId}`
        : `${API_BASE}/reminders`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to fetch reminders: ${text}`);
      }

      const data = await response.json();
      setReminders(data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      showError("Couldn't load reminders.");
    }
  };

  // Auth Watcher
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user);
        await fetchCars(user);
      } else {
        setUser(null);
        setRecords([]);
        setCars([]);
        setSelectedCar(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Selected Car Watcher - fetch records when user picks a different car
  useEffect(() => {
    if (selectedCar && user) {
      fetchRecords(user, selectedCar.id);
      fetchReminders(user, selectedCar.id);
    } else if (user) {
      fetchReminders(user, null);
    }
  }, [selectedCar, user]);

  // Theme Watcher - load saved theme from localStorage on app start
  useEffect(() => {
    const savedMode = localStorage.getItem("themeMode");
    if (savedMode) setMode(savedMode);
  }, [setMode]);

  const handleToggleMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", next);
      return next;
    });
  };

  return (
    <>
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: {
            height: "100%",
            backgroundColor: theme.palette.background.default,
          },
          body: {
            height: "100%",
            margin: 0,
            backgroundColor: theme.palette.background.default,
          },
          "#root": {
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
          },
        }}
      />

      {user ? (
        <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
          <Sidebar
            user={user}
            cars={cars}
            selectedCar={selectedCar}
            onSelectCar={setSelectedCar}
            onCarAdded={(newCar) => {
              setCars((prev) => [...prev, newCar]);
              setSelectedCar(newCar);
            }}
            onDeleteCarRequest={handleDeleteCarRequest}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setMobileNavOpen(false);
            }}
            mode={mode}
            onToggleMode={handleToggleMode}
            onLogout={() => auth.signOut()}
            mobileOpen={mobileNavOpen}
            onCloseMobile={() => setMobileNavOpen(false)}
          />

          <Box
            component="main"
            sx={{
              flex: 1,
              overflowY: "auto",
              position: "relative",
              zIndex: 1,
              px: { xs: 2.5, md: 5 },
              py: { xs: 2.5, md: 4 },
            }}
          >
            {!isDesktop && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <IconButton onClick={() => setMobileNavOpen(true)} edge="start">
                  <MenuRoundedIcon />
                </IconButton>
                <Typography sx={{ fontWeight: 800 }}>AutoLogg</Typography>
              </Box>
            )}

            {carsLoading ? (
              <Skeleton variant="rounded" height={90} sx={{ mb: 3 }} />
            ) : (
              <VehicleHeroCard car={selectedCar} records={records} currentMileage={currentMileage} />
            )}

            <Divider sx={{ my: 3 }} />

            {activeTab === "maintenance" && (
              <>
                <AddMaintenance
                  user={user}
                  selectedCar={selectedCar}
                  records={records}
                  user_id={user.uid}
                  onAdd={handleAddRecord}
                />

                {selectedCar && (
                  <MaintenanceRecommendations user={user} selectedCar={selectedCar} />
                )}

                <Box sx={{ mt: 2 }}>
                  <MaintenanceList
                    records={records}
                    loading={recordsLoading}
                    user_id={user.uid}
                    onDelete={handleDeleteRecord}
                    onEdit={handleEditRecord}
                    newlyAddedId={newlyAddedId}
                  />

                  <AutoLoggAssistant
                    records={records}
                    onAddRecord={handleAddRecord}
                    vehicleProfile={selectedCar}
                    currentMileage={currentMileage}
                    onCreateReminder={handleCreateReminder}
                    onCreateIssue={handleCreateIssue}
                  />
                </Box>
              </>
            )}

            {activeTab === "insights" && (
              <InsightsDashboard
                records={records}
                selectedCar={selectedCar}
                currentMileage={currentMileage}
              />
            )}

            {activeTab === "reminders" && (
              <RemindersSection
                carList={cars}
                selectedCar={selectedCar}
                reminders={reminders}
                currentMileage={currentMileage}
                onCreateReminder={handleCreateReminder}
                onUpdateReminder={handleUpdateReminder}
                onDeleteReminder={handleDeleteReminder}
              />
            )}

            <Dialog
              open={Boolean(editingRecord)}
              onClose={() => setEditingRecord(null)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Edit Maintenance Record</DialogTitle>
              <DialogContent dividers>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="edit-service-label">Service Type</InputLabel>
                  <Select
                    labelId="edit-service-label"
                    label="Service Type"
                    value={editingRecord?.service_type || ""}
                    onChange={(e) =>
                      setEditingRecord({
                        ...editingRecord,
                        service_type: e.target.value,
                      })
                    }
                  >
                    {serviceTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  margin="normal"
                  label="Mileage"
                  type="number"
                  value={editingRecord?.mileage || ""}
                  onChange={(e) =>
                    setEditingRecord({
                      ...editingRecord,
                      mileage: e.target.value,
                    })
                  }
                  inputProps={{ min: 0, max: 1000000 }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Cost"
                  type="number"
                  placeholder="85.00"
                  value={editingRecord?.cost ?? ""}
                  onChange={(e) =>
                    setEditingRecord({
                      ...editingRecord,
                      cost: e.target.value,
                    })
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Date"
                  type="date"
                  value={editingRecord?.date || ""}
                  onChange={(e) =>
                    setEditingRecord({
                      ...editingRecord,
                      date: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setEditingRecord(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => handleSaveEditedRecord(editingRecord)}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      ) : (
        <Login onLogin={setUser} />
      )}

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        destructive={confirmState?.destructive}
        onConfirm={handleConfirmAccept}
        onClose={() => setConfirmState(null)}
      />
    </>
  );
}

export default App;
